import api from '../../services/api';

/**
 * The listing. GET with the filters in the query string, as always, except
 * when the advanced filter is on: a pasted list of 500 reference ids runs to
 * 10 KB and nginx refuses URLs past 8 KB (414), so then the same parameters
 * travel in the body of a POST, which the endpoint reads identically.
 */
export const fetchDeliveriesApi = (params) => {
  if (params?.advanced_filter_field && params?.advanced_filter_values) {
    return api.post('api/delivery-resume/', params);
  }
  return api.get('api/delivery-resume/', { params });
};

export const changeDriverApi = (payload) => {
  return api.post('deliveries/change_driver/', payload);
};

export const massiveChangeStatusApi = (payload) => {
  return api.post('deliveries/change_status/', payload);
};

export const fulfillDeliveriesApi = (payload) => {
  return api.post('deliveries/fulfill_deliveries/', payload);
};

// A POST only because the id list can be long: it reads, so losing its answer
// is a dead server, not a change that may have been applied.
export const getSelectedDeliveriesApi = (payload) => {
  return api.post('api/get-selected-deliveries/', payload, { readOnly: true });
};

/**
 * Sends shipments to their courier without the preparation rules: the backend
 * fills in the measures and weights the bultos are missing.
 *
 * Takes at most 100 ids. Answers right away with what was queued and what was
 * skipped, and why; the courier is called by a worker.
 *
 * @param {number[]} deliveryIds
 * @returns {Promise<{data: {requested: number, queued: Array<object>, skipped: Array<object>}}>}
 */
export const dispatchDeliveriesApi = (deliveryIds) => {
  return api.post('deliveries/dispatch_deliveries/', { delivery_ids: deliveryIds });
};

/**
 * Hands shipments to another courier, with the rules of the order detail's
 * «Cambiar courier».
 *
 * Takes at most 100 ids. Answers right away with what was queued — and how:
 * back to «Por asignar» under the seller's stand-by, or sent to the new courier
 * — and what was skipped, and why; the change runs in a worker.
 *
 * @param {number[]} deliveryIds
 * @param {string} courier - The courier's `system_name`, e.g. `starken`.
 * @returns {Promise<{data: {requested: number, courier: string, queued: Array<object>, skipped: Array<object>}}>}
 */
export const changeDeliveriesCourierApi = (deliveryIds, courier) => {
  return api.post('deliveries/change_deliveries_courier/', { delivery_ids: deliveryIds, courier });
};

/**
 * Sends shipments to one of the operator's pickup points, with the rules of
 * the order detail's «Entregar en punto de retiro»: only the pending and the
 * waiting ones change, and the customer's address is kept.
 *
 * Takes at most 100 ids. Answers with what changed and what was skipped, and
 * why. No worker: each change is written on the spot.
 *
 * @param {number[]} deliveryIds
 * @param {number} pickupPointId
 * @returns {Promise<{data: {requested: number, pickup_point: {id: number, name: string},
 *   redirected: Array<object>, skipped: Array<object>}}>}
 */
export const changeDeliveriesPickupPointApi = (deliveryIds, pickupPointId) => {
  return api.post('deliveries/change_deliveries_pickup_point/', {
    delivery_ids: deliveryIds,
    pickup_point_id: pickupPointId,
  });
};

/**
 * The couriers an operator can route through: the session's own, or, for the
 * staff, the operator in `adminId`. The backend ignores `adminId` for anyone
 * else.
 *
 * @param {number|string|null} [adminId]
 * @returns {Promise<{data: Array<{id: number, courier: string}>}>}
 */
export const getCourierOptionsApi = (adminId) => {
  return api.get('companies/available_couriers/', adminId ? { params: { admin_id: adminId } } : undefined);
};

/**
 * The catalogs behind the deliveries filter bar.
 *
 * `companies` is the useful part: the admin's own companies, `{id,
 * social_reason}`, which is what the `company` filter has to send — the backend
 * compares it against `companies_company.id`.
 *
 * @returns {Promise<{data: {companies: Array<object>, delivery_statuses: Array<object>}}>}
 */
export const getDeliveryFilterOptionsApi = () => {
  return api.get('delivery_params/api/delivery-resume/');
};

/**
 * Queues the deliveries report for the current filter.
 *
 * Answers immediately: the spreadsheet is built by a Celery worker that reports
 * back over the socket, on the `download` channel.
 *
 * @param {object} payload - `start_date`, `end_date`, `company`, `search`, `delivery_status`
 */
export const exportDeliveriesApi = (payload) => {
  return api.post('api/download-delivery-resume/', payload);
};

/**
 * Queues the status-history report. Staff only — the endpoint is behind
 * `IsStaffAdminUser`. Reports back on the `download_history` channel.
 *
 * @param {object} payload - `start_date`, `end_date`, `delivery_status`
 */
export const exportStatusHistoryApi = (payload) => {
  return api.post('deliveries/download_status_history/', payload);
};

/**
 * @typedef {object} SingleOrderPayload
 * @property {number|null} seller_id
 * @property {number} warehouse_id
 * @property {string} reference_id
 * @property {{name: string, last_name: string, phone: string, email: string, rut: string}} recipient
 * @property {object} shipping_address - Shape depends on the country
 * @property {string} delivery_shipping_method
 * @property {number|string} packages - Number of parcels (items) the order creates
 * @property {string} notes
 * @property {number|string} weight - In grams
 */

/**
 * Creates a single order along with its parcels.
 *
 * @param {SingleOrderPayload} payload
 */
export const createSingleOrderApi = (payload) => {
  return api.post('create_single_order/', payload);
};

/**
 * Hands over a spreadsheet that creates many orders at once.
 *
 * Answers 202 as soon as the file is stored: the orders are created by a worker
 * and reported over the socket. The synchronous endpoint next to this one did
 * the whole file inside the request, which a few hundred rows outlast.
 *
 * @param {FormData} formData - `excel_file`, `seller_id`, `warehouse_id`, `country`
 * @param {(event: ProgressEvent) => void} [onUploadProgress]
 */
export const uploadOrdersExcelApi = (formData, onUploadProgress) => {
  return api.post('upload_excel_orders_async/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
};
